import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import EmojiFrequentIcon from '@atlaskit/icon/core/clock';
import CrossIcon from '@atlaskit/icon/core/cross';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { RECENT_SEARCH_KEY, removeRecentSearch } from '@atlassian/dynamic-cards-search-common';

import { SearchLink, type SearchLinkProps } from '../../../../../common/ui/search-link';
import { useBootstrap, useDropdown, useSearchActions } from '../../../../../controllers/store';
import {
	useDropdownActions,
	useRecentSearchResults,
	useResultCount,
} from '../../../../../controllers/store/dropdown';

import { messages } from './messages';

const iconStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.xsmall',
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const RecentSearchResults = (): React.JSX.Element => {
	const { setSearchQuery } = useSearchActions();
	const [resultCount] = useResultCount();
	const [, { setShowDropdown }] = useDropdown();

	const onClick = (query: string) => {
		setShowDropdown(false);
		setSearchQuery(query);
	};

	const [recentSearches] = useRecentSearchResults();

	return (
		<>
			{recentSearches.slice(0, resultCount.recentSearch).map((result, index) => (
				<SearchLink
					title={result.title}
					icon={<RecentSearchIcon />}
					onClick={() => onClick(result.title)}
					key={`${index}-${result.title}`}
					rightPanel={<RecentSearchRightPanel title={result.title} />}
				/>
			))}
		</>
	);
};

export const RecentSearchIcon = (): React.JSX.Element => {
	const intl = useIntl();
	return (
		<Box xcss={iconStyles}>
			<EmojiFrequentIcon
				color="currentColor"
				label={intl.formatMessage(messages.recentSearchIconLabel)}
				spacing="spacious"
			/>
		</Box>
	);
};

const recentSearchRightPanelStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	font: 'font.body.small',
});

const subheadingStyles = xcss({
	marginRight: 'space.150',
});

export const RecentSearchRightPanel = ({ title }: { title: string }): React.JSX.Element => {
	const intl = useIntl();
	const [{ user, primaryProduct }] = useBootstrap();
	const { setRecentSearchResults } = useDropdownActions();

	const onClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			event.stopPropagation();
			if (user?.id && primaryProduct) {
				const newRecentSearches: SearchLinkProps[] = removeRecentSearch(
					title,
					user.id,
					primaryProduct,
					RECENT_SEARCH_KEY,
				).map((searchTitle) => ({
					// Todo: cleanup this so that we only need to pass the search title.
					title: searchTitle,
					icon: <></>,
					onClick: () => null,
				}));
				setRecentSearchResults(newRecentSearches);
			}
		},
		[primaryProduct, title, setRecentSearchResults, user?.id],
	);

	return (
		<Box xcss={recentSearchRightPanelStyles}>
			<Box as="span" xcss={subheadingStyles}>
				{intl.formatMessage(messages.removeRecentSearchSubheading)}
			</Box>
			<IconButton
				icon={(iconProps) => (
					<CrossIcon
						{...iconProps}
						label={intl.formatMessage(messages.removeRecentSearchIcon)}
						color="currentColor"
					/>
				)}
				spacing={'compact'}
				onClick={onClick}
				label={intl.formatMessage(messages.removeRecentSearchIcon)}
				appearance={'subtle'}
			/>
		</Box>
	);
};
